import type { SupplierInput, SupplierView } from '@chukua/common';
import { Button } from 'antd';
import { Package, Plus, ShieldAlert } from 'lucide-react';
import { Navigate } from 'react-router';
import { TableSkeleton } from '../../../../../shared/components/TableSkeleton';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { buildDirectoryColumns } from '../../../components/build-directory-columns';
import { DirectoryTable } from '../../../components/DirectoryTable';
import { useCanManageService } from '../../../hooks/use-can-manage-service';
import { useDirectoryDrawer } from '../../../hooks/use-directory-drawer';
import { useDirectoryTags } from '../../../hooks/use-directory-tags';
import { SupplierFormDrawer } from '../components/SupplierFormDrawer';
import { useSuppliers } from '../hooks/use-suppliers';

/** The directory of parts suppliers (gitaumoses4/chukua#173): who to order parts from, their categories and specialities. Deactivated, never deleted. */
export function SuppliersPage() {
  const catalogue = useDirectoryTags('supplier_category');
  const { state, list, isRefreshing, saveError, saving, resetSaveError, create, update, deactivate, reactivate } = useSuppliers(catalogue.refresh);
  const canManage = useCanManageService();
  const drawer = useDirectoryDrawer<SupplierView>(resetSaveError);

  if (state.status === 'forbidden') return <Navigate to="/" replace />;

  const actions = canManage ? { onEdit: drawer.openDrawer, onDeactivate: deactivate, onReactivate: reactivate } : undefined;
  const newButton = canManage && (
    <Button type="primary" icon={<Plus className="size-4" />} onClick={() => drawer.openDrawer()}>
      New supplier
    </Button>
  );

  return (
    <div className="flex flex-col gap-4">
      {newButton && <div className="flex justify-end">{newButton}</div>}
      {state.status === 'error' && (
        <EmptyState icon={ShieldAlert} title="Couldn't load suppliers" description="Something went wrong loading the directory. Try reloading the page." />
      )}
      {state.status === 'loading' && <TableSkeleton label="Loading suppliers" withSearch />}
      {state.status === 'loaded' && (
        <DirectoryTable
          page={state.page}
          columns={buildDirectoryColumns({
            list,
            place: { title: 'Location', value: (row: SupplierView) => row.location, sortKey: 'location' },
            tags: { title: 'Categories', filterKey: 'category', catalogue: catalogue.tags, values: (row: SupplierView) => row.categories },
            actions,
          })}
          list={list}
          isRefreshing={isRefreshing}
          newButton={newButton}
          emptyIcon={Package}
          emptyTitle="No suppliers yet"
          emptyDescription="Add a parts supplier to order from, with its categories and specialities."
          searchPlaceholder="Search name or location"
          noMatchTitle="No matching suppliers"
        />
      )}
      <SupplierFormDrawer
        open={drawer.open}
        editing={drawer.editing}
        catalogue={catalogue.tags}
        error={saveError}
        submitting={saving}
        onSubmit={(values) => (drawer.editing ? update(drawer.editing.id, values as SupplierInput) : create(values as SupplierInput))}
        onClose={drawer.close}
      />
    </div>
  );
}
